<template>
    <div>
        <page-header>
            Collections
            <template v-slot:actions>
                <vx-button @click="showOverview = !showOverview"
                    data-test="toggle-overview"
                    :aria-label="`${showOverview ? 'Hide' : 'Show'} Overview`"
                    type="button"
                    color="muted-dark"
                    size="sm">
                    <span class="hidden sm:inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                    <font-awesome-icon widthAuto class="hidden! sm:inline-block!"
                        :icon="faCircleQuestion"></font-awesome-icon>
                    <font-awesome-icon widthAuto class="inline-block! sm:hidden!"
                        :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <group-collections-overview v-show="showOverview" :orgUnitId="props.orgUnitId" class="mb-14"></group-collections-overview>

        <vx-alert v-if="!props.orgUnitId && list.length >= 20"
            :closeable="false"
            shadow
            class="mt-14 mb-8">
            Reached maximum of 20 collections
        </vx-alert>

        <group-collection-controls v-model="filters"
            :orgUnitId="props.orgUnitId"
            @apply="applyFilters"
            @reset="resetFilters"
            :list="list"
            :loading="loading"></group-collection-controls>

        <page-loading v-if="loading && !list.length"></page-loading>
        <empty-state v-else-if="!loading && !list.length">
            No Collections yet.
            <vx-button v-if="isAdminOrOwner || isOrgUnitAdmin"
                data-test="new-button"
                :href="props.orgUnitId ? `/settings/teams/${teamId}/org-units/${props.orgUnitId}/group-collections/new` : `/settings/teams/${teamId}/group-collections/new`">
                New Collection
                <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
            </vx-button>
        </empty-state>
        <empty-state v-else-if="!filteredList.length" @reset="resetFilters" />

        <vx-table v-else-if="list.length"
            :class="isAdminOrOwner || isOrgUnitAdmin ? 'xl:grid-cols-3-auto' : 'xl:grid-cols-2-auto'"
            rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div>
                    Name
                </div>
                <div>
                    Groups
                </div>
            </vx-table-row>

            <group-collection-row v-for="(item) in filteredList"
                :modelValue="item"
                :key="item.id"
                @deleted="removeFromList($event)"></group-collection-row>
        </vx-table>
    </div>
</template>

<script setup>
import {
    faCircleChevronDown,
    faCircleChevronUp,
    faCirclePlus,
    faCircleQuestion,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { onMounted, ref , computed } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import GroupCollectionControls from './GroupCollectionControls.vue';
import GroupCollectionsOverview from './GroupCollectionsOverview.vue';
import GroupCollectionRow from './GroupCollectionRow.vue';
import { groupCollectionsClient } from '~/services';
import { useGroupCollectionsGroupsCount } from '~/composables/useGroupCollectionsGroupsCount';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
});

const { roleOnCurrentOrgUnit } = useCurrentGroup();
const toasted = useToasted();

const showOverview = ref(true);

const teamId = Spark.state.currentTeam.id;
const isAdminOrOwner = Spark.isAdminOrOwner;

const loading = ref(false);
const filters = ref({});
const list = ref([]);

const isOrgUnitAdmin = computed(() => ['admin', 'owner'].includes(roleOnCurrentOrgUnit));

const filteredList = computed(() => {
    return list.value.filter((item) => {
        const name = item.name.toLocaleLowerCase().includes(filters.value.query.toLocaleLowerCase());

        return name;
    }).sort((a, b) => {
        if (filters.value.sort_field === 'name') {
            return filters.value.sort_dir === 'desc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        } else if (['created_at', 'updated_at'].includes(filters.value.sort_field)) {
            return filters.value.sort_dir === 'desc' ? new Date(b[filters.value.sort_field]) - new Date(a[filters.value.sort_field]) : new Date(a[filters.value.sort_field]) - new Date(b[filters.value.sort_field]);
        } else {
            return 0;
        }
    })
});

const defaultFilters = () => ({
    query: '',
    sort_field: 'created_at',
    sort_dir: 'desc'
});

const resetFilters = () => {
    filters.value = defaultFilters();
};

const applyFilters = () => {
    filters.value = {
        ...filters.value,
    }
}

onMounted(() => {
    resetFilters();
    loadData();
});

const { fetchGroupCollectionsGroupsCount } = useGroupCollectionsGroupsCount();

const loadData = async () => {
    try {
        const context = {
            teamId: teamId,
            orgUnitId: props.orgUnitId
        }
        loading.value = true;
        const response = await groupCollectionsClient.getGroupCollections(context, { ...filters.value });
        list.value = response.data.data;

        fetchGroupCollectionsGroupsCount(context, list.value.map((groupCollection) => groupCollection.id))
            .catch((e) => toasted.error(catchRequestError(e)));
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        loading.value = false;
    }
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.id !== itemId);

    if (!list.value.length) {
        loadData();
    }
};

</script>
